import { useState } from "react";
import * as Icons from "lucide-react";
import { Caption } from "@/components/Common";
import { humanize } from "@/lib/api";

const HIDDEN = ["id", "_id", "user_id", "job_id", "session_id", "created_at", "updated_at"];
const IMG_KEY = /^(logo|logo_url|company_logo|image|image_url|icon|icon_url|picture|avatar|thumbnail|favicon)$/i;
const isPrim = (v) => ["string", "number", "boolean"].includes(typeof v);
const isSvg = (v) => typeof v === "string" && /^\s*<svg[\s>]/i.test(v);
const isImgUrl = (v) => typeof v === "string" && (/^data:image\//i.test(v) || /^https?:\/\/\S+$/i.test(v.trim()));
const toPascal = (s) => String(s).replace(/(^|[-_\s])(\w)/g, (_, __, c) => c.toUpperCase()).replace(/Outline$/, "");
const embeddedSrc = (v) => typeof v === "string" && (v.match(/^\s*<img[^>]*\bsrc=["']([^"']+)["']/i)?.[1] || v.match(/^\s*!\[[^\]]*\]\(([^)\s]+)\)\s*$/)?.[1]);

const Fallback = ({ alt }) => <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#D4AF37]/25 bg-[#27272A] font-display font-bold text-[#D4AF37]">{alt ? alt.slice(0, 1).toUpperCase() : <Icons.Building2 className="h-4 w-4" />}</span>;

const Img = ({ v: raw, alt }) => {
  const [failed, setFailed] = useState(false);
  const v = embeddedSrc(raw) || raw;
  if (failed) return <Fallback alt={alt} />;
  const cls = "h-10 w-10 shrink-0 rounded-lg border border-[#27272A] bg-white object-contain p-1";
  if (isSvg(v)) return <img src={`data:image/svg+xml;utf8,${encodeURIComponent(v)}`} alt={alt || ""} className={cls} onError={() => setFailed(true)} />;
  if (isImgUrl(v)) return <img src={v.trim()} alt={alt || ""} className={cls} onError={() => setFailed(true)} />;
  const I = typeof v === "string" && Icons[toPascal(v)];
  if (I) return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#D4AF37]/10"><I className="h-4 w-4 text-[#D4AF37]" /></span>;
  return <Fallback alt={alt} />;
};

function renderRich(value) {
  if (value == null || value === "") return null;
  if (isSvg(value) || embeddedSrc(value) || (typeof value === "string" && /^data:image\//i.test(value))) return <Img v={value} />;
  if (isPrim(value)) return <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{String(value)}</p>;
  if (Array.isArray(value)) {
    if (!value.length) return null;
    if (value.every(isPrim))
      return (
        <ul className="space-y-1.5">
          {value.map((v, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed text-zinc-300">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" />
              <span className="whitespace-pre-wrap">{String(v)}</span>
            </li>
          ))}
        </ul>
      );
    return (
      <div className="space-y-3">
        {value.map((v, i) => (
          <div key={i} className="rounded-lg border border-[#27272A] bg-[#09090B]/60 p-3">
            {renderRich(v)}
          </div>
        ))}
      </div>
    );
  }
  const all = Object.entries(value).filter(([k, v]) => !HIDDEN.includes(k) && v != null && v !== "");
  const imgEntry = all.find(([k, v]) => IMG_KEY.test(k) && typeof v === "string");
  const entries = all.filter(([k]) => !imgEntry || k !== imgEntry[0]);
  const name = value.company || value.company_name || value.name || value.title;
  const body = (
    <div className="min-w-0 flex-1 space-y-3">
      {entries.map(([k, v]) => (
        <div key={k}>
          <Caption className="mb-1">{humanize(k)}</Caption>
          {renderRich(v)}
        </div>
      ))}
    </div>
  );
  if (!imgEntry) return body;
  return <div className="flex gap-3" data-testid="rich-image-row"><Img v={imgEntry[1]} alt={typeof name === "string" ? name : ""} />{body}</div>;
}

export const Rich = ({ value }) => renderRich(value);

const pick = (o, keys) => keys.map((k) => o[k]).find((v) => v != null && v !== "");
const H = ["heading", "title", "label", "name"];
const B = ["body", "content", "text", "summary", "description"];
const L = ["items", "bullets", "points", "list"];

export const Section = ({ s }) => {
  if (s == null || isPrim(s)) return <Rich value={s} />;
  const heading = pick(s, H);
  const body = pick(s, B);
  const items = pick(s, L);
  const rest = Object.fromEntries(Object.entries(s).filter(([k]) => ![...H, ...B, ...L].includes(k)));
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#141417] p-4 fade-up" data-testid="result-section">
      {heading && <h4 className="mb-2 font-display text-base font-semibold text-[#F3C649]">{heading}</h4>}
      <div className="space-y-3">
        <Rich value={body} />
        <Rich value={items} />
        {Object.keys(rest).length > 0 && <Rich value={rest} />}
      </div>
    </div>
  );
};

export const Sections = ({ result }) => {
  if (!result) return null;
  const sections = result.sections || result.result?.sections;
  return (
    <div className="space-y-3">
      {result.title && <h3 className="font-display text-xl text-zinc-100">{result.title}</h3>}
      {Array.isArray(sections) ? sections.map((s, i) => <Section key={i} s={s} />) : <Rich value={result} />}
    </div>
  );
};
